"use client";
import PropTypes from "prop-types";
import { useRouter } from "next/navigation";
import Breadcrumb from "../../components/atoms/breadCrumb";
import Typography from "../../components/atoms/typography";
import { CUSTOMER_REVIEWS_N_TESTIMONIALS, WHAT_OUR_USERS_SAY_ABOUT_US } from "../../constants/common/common";
import TestimonialsHeader from "./testimonialsComponents/TestimonialsHeader";
import { useEffect, useState } from "react";
import { getCategoryReviewsData, getTestimonialsData } from "../../api/testimonialsApi";
import { getMonthNumber, restrictAutoScrollingOnLoad } from "../../utils/common";
import { PageTypes, setPageType } from "../../utils/fnpPageType";
import Pagination from "./testimonialsComponents/Pagination";
import TestimonialCard from "../../components/organisms/testimonialCard";
import RatingStars from "../../components/molecules/ratingStars";
import LottieLoader from "../../components/atoms/lottieLoader";
import CustomImage from "../../components/atoms/customImage";

/** */
const TestimonialsPage = ({
  responseData,
  breadcrumbs,
  isMobile,
  catalogId,
  isShowSchema,
  pageType = "",
  ratingValue,
  reviewCount,
  selectedYear,
  selectedMonth,
}) => {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(responseData?.currentPage);
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [testimonialReviews, setTestimonialReviews] = useState(
    Array.isArray(responseData?.data) ? responseData.data : [],
  );
  const review = {};
  review["ratingValue"] = ratingValue;

  // Check if there are any reviews
  const hasReviews =
    (Array.isArray(testimonialReviews) && testimonialReviews.length > 0) ||
    (Array.isArray(responseData?.data) && responseData.data.length > 0);
  const hasValidRating = ratingValue != null && ratingValue > 0;
  const hasValidReviewCount = reviewCount != null && reviewCount > 0;
  const shouldShowRatingSection =
    (pageType === "category" || pageType === "product") && hasValidRating && hasValidReviewCount;

  useEffect(() => {
    restrictAutoScrollingOnLoad();
    setPageType(PageTypes.TESTIMONIALS);
  }, []);

  /** */
  const handleMonthYear = (year, month) => {
    router.push(`/info/testimonials/${year}/${month.toLowerCase()}`);
    setTestimonialReviews([]);
    setIsShowLoading(true);
  };
  /** */
  const onHandlePageChange = async (newPage) => {
    setIsShowLoading(true);
    setTestimonialReviews([]);

    const searchParams = new URLSearchParams(window.location.search);
    const params = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    setCurrentPage(newPage);

    let response = {};

    if (pageType === "category") {
      response = await getCategoryReviewsData({
        categoryId: params?.categoryId || "",
        pageNumber: newPage - 1,
        size: 12,
      });
    } else {
      const testimonialOptions = {
        pageType: pageType || "dateRange",
        requestType: "csr",
        pageNumber: newPage - 1,
      };

      if (pageType === "product") {
        testimonialOptions.categoryId = params?.categoryId;
        testimonialOptions.productId = params?.productId;
      } else if (selectedMonth && selectedYear) {
        testimonialOptions.month = getMonthNumber(selectedMonth);
        testimonialOptions.year = selectedYear;
      }

      response = await getTestimonialsData(testimonialOptions);
    }

    setTestimonialReviews(Array.isArray(response?.data) ? response.data : []);
    setIsShowLoading(false);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  return (
    <>
      <div className="px-16 md:px-40">
        {isShowLoading && <LottieLoader />}
        {breadcrumbs && !isMobile && (
          <Breadcrumb items={breadcrumbs} catalogId={catalogId} isShowSchema={isShowSchema} />
        )}

        {hasReviews && (
          <div className="my-20 flex flex-wrap justify-between md:mt-0 md:flex-nowrap">
            <div className="flex flex-col md:gap-16">
              <div className="flex flex-col justify-start gap-12 md:flex-row md:items-center">
                <Typography Tag={"h2"} variant="h2" classes="text-14 md:text-24 text-fnp-500 font-500">
                  {CUSTOMER_REVIEWS_N_TESTIMONIALS}
                </Typography>
                {shouldShowRatingSection && (
                  <div className="flex items-center gap-8">
                    <RatingStars ratings={review?.ratingValue} stars={5} />
                    <Typography
                      variant="p"
                      Tag={"p"}
                      classes="cursor-pointer text-12 md:text-14 font-500 text-offer-900"
                    >
                      {`${review?.ratingValue}`}
                    </Typography>
                    <div className="h-3 w-3 rounded-full bg-grey-400" />
                    <Typography
                      variant="p"
                      Tag={"p"}
                      classes="cursor-pointer text-10 md:text-14 font-50 text-grey-400 "
                    >
                      {`${reviewCount} Reviews`}
                    </Typography>
                  </div>
                )}
              </div>
              {!isMobile && <div className="text-20 font-500 text-black-100">{WHAT_OUR_USERS_SAY_ABOUT_US}</div>}
            </div>
            {pageType !== "category" && pageType !== "product" && (
              <div className="w-full md:w-auto">
                <TestimonialsHeader
                  pageType={pageType}
                  testimonialsCount={testimonialReviews?.length}
                  handleMonthYear={handleMonthYear}
                  year={selectedYear}
                  month={selectedMonth}
                  currentPage={currentPage}
                  onHandlePageChange={onHandlePageChange}
                  totalPages={responseData?.totalPages}
                  isMobile={isMobile}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex w-full flex-col gap-20">
          {Array.isArray(testimonialReviews) && testimonialReviews.length > 0 ? (
            testimonialReviews.map((item) => {
              return <TestimonialCard review={item} key={item?.respondedDateMillis} isShowImage={true} />;
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-40">
              <CustomImage
                src="/icons/no-reviews-found.svg"
                alt="No testimonials found"
                width={240}
                height={240}
                isPriority={false}
              />
              <Typography
                Tag="p"
                variant="p"
                classes="leading-28 mb-8 text-center text-16 font-600 leading-22 text-fnp-500 md:text-20"
              >
                No Reviews Found
              </Typography>
            </div>
          )}
        </div>
      </div>
      <div className="mt-20 flex items-center justify-center border-t-1 border-thinLightGrey pt-20">
        {Array.isArray(responseData?.data) && responseData.data.length > 0 && responseData?.totalPages > 0 && (
          <Pagination
            noOfPages={responseData?.totalPages}
            page={currentPage}
            defaultPage={1}
            onChange={onHandlePageChange}
            isMobile={isMobile}
            customClasses={{ container: "border rounded-8 overflow-hidden" }}
          />
        )}
      </div>
    </>
  );
};

TestimonialsPage.propTypes = {
  responseData: PropTypes.object.isRequired,
  breadcrumbs: PropTypes.array,
  isMobile: PropTypes.bool.isRequired,
  catalogId: PropTypes.string.isRequired,
  isShowSchema: PropTypes.bool.isRequired,
  pageType: PropTypes.string,
  ratingValue: PropTypes.number,
  reviewCount: PropTypes.number,
  selectedYear: PropTypes.string,
  selectedMonth: PropTypes.string,
};
export default TestimonialsPage;
